import React, { useState, useEffect } from 'react';
import { UserDocument } from '@curbside-quest/shared';

// Helper function to safely convert Firebase timestamps to Date objects
const safeToDate = (timestamp: any): Date => {
  if (!timestamp) return new Date();
  
  // If it's a Firestore Timestamp with toDate method
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  
  // If it's a plain object with seconds (from Firebase Functions)
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000);
  }
  
  // If it's already a Date object
  if (timestamp instanceof Date) {
    return timestamp;
  }
  
  // If it's a number (milliseconds)
  if (typeof timestamp === 'number') {
    return new Date(timestamp);
  }
  
  // If it's a string
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  
  // Fallback to current date
  return new Date();
};

interface UserWithActions extends UserDocument {
  banned?: boolean;
  banReason?: string;
  bannedAt?: Date;
  bannedBy?: string;
  banUntil?: Date;
}

export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserWithActions[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'banned'>('all');
  const [selectedUser, setSelectedUser] = useState<UserWithActions | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, [filter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      // This would call the Firebase function
      // const result = await functions.httpsCallable('getUsers')({ filter });
      // setUsers(result.data.users);
      
      // Mock data for now
      const mockUsers: UserWithActions[] = [
        {
          id: 'user1',
          email: 'john.doe@example.com',
          displayName: 'John Doe',
          authProvider: 'google',
          subscriptionTier: 'free',
          goldCoins: 150,
          farAwayTokens: 3,
          locationRadius: 2000,
          createdAt: { seconds: Math.floor(new Date('2024-01-15').getTime() / 1000), nanoseconds: 0, toDate: () => new Date('2024-01-15'), toMillis: () => new Date('2024-01-15').getTime(), isEqual: () => false } as any,
          updatedAt: { seconds: Math.floor(new Date('2024-01-20').getTime() / 1000), nanoseconds: 0, toDate: () => new Date('2024-01-20'), toMillis: () => new Date('2024-01-20').getTime(), isEqual: () => false } as any
        },
        {
          id: 'user2',
          email: 'jane.smith@example.com',
          displayName: 'Jane Smith',
          authProvider: 'magic-link',
          subscriptionTier: 'premium',
          goldCoins: 320,
          farAwayTokens: 20,
          locationRadius: 5000,
          createdAt: { seconds: Math.floor(new Date('2024-01-10').getTime() / 1000), nanoseconds: 0, toDate: () => new Date('2024-01-10'), toMillis: () => new Date('2024-01-10').getTime(), isEqual: () => false } as any,
          updatedAt: { seconds: Math.floor(new Date('2024-01-18').getTime() / 1000), nanoseconds: 0, toDate: () => new Date('2024-01-18'), toMillis: () => new Date('2024-01-18').getTime(), isEqual: () => false } as any,
          banned: true,
          banReason: 'Spam reports',
          bannedAt: new Date('2024-01-18'),
          bannedBy: 'admin1'
        }
      ];

      setUsers(filter === 'banned' ? mockUsers.filter(u => u.banned) : mockUsers);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBanUser = async (userId: string, reason: string, duration?: number) => {
    try {
      setActionLoading(userId);
      // This would call the Firebase function
      // await functions.httpsCallable('banUser')({ userId, reason, duration });
      
      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { 
              ...user, 
              banned: true, 
              banReason: reason,
              bannedAt: new Date(),
              banUntil: duration ? new Date(Date.now() + duration * 24 * 60 * 60 * 1000) : undefined
            }
          : user
      ));
      
      setSelectedUser(null);
      console.log('User banned:', userId, reason);
    } catch (error) {
      console.error('Error banning user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnbanUser = async (userId: string) => {
    try {
      setActionLoading(userId);
      // This would call the Firebase function
      // await functions.httpsCallable('unbanUser')({ userId, reason });
      
      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { 
              ...user, 
              banned: false,
              banReason: undefined,
              bannedAt: undefined,
              banUntil: undefined
            }
          : user
      ));
      
      setSelectedUser(null);
      console.log('User unbanned:', userId);
    } catch (error) {
      console.error('Error unbanning user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">
          User Management ({users.length})
        </h2>
        <div className="flex items-center space-x-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'all' | 'banned')}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">All Users</option>
            <option value="banned">Banned Users</option>
          </select>
          <button
            onClick={loadUsers}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Users List */}
        <div className="space-y-4">
          {users.map((user) => (
            <div
              key={user.id}
              className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                selectedUser?.id === user.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              } ${user.banned ? 'bg-red-50 border-red-200' : ''}`}
              onClick={() => setSelectedUser(user)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className="font-medium text-gray-900">{user.displayName}</h3>
                    {user.banned && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Banned
                      </span>
                    )}
                    {user.subscriptionTier === 'premium' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Premium
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{user.email}</p>
                  <div className="flex items-center text-xs text-gray-500 space-x-4">
                    <span>{user.goldCoins} coins</span>
                    <span>{user.authProvider}</span>
                    <span>Joined {safeToDate(user.createdAt).toLocaleDateString()}</span>
                  </div>
                  
                  {user.banned && user.banReason && (
                    <div className="mt-2">
                      <span className="text-xs text-red-600">
                        Banned: {user.banReason}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {users.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-lg mb-2">👥</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {filter === 'banned' ? 'No banned users' : 'No users found'}
              </h3>
              <p className="text-gray-500">
                {filter === 'banned' 
                  ? 'All users are in good standing!'
                  : 'No users match the current filter.'
                }
              </p>
            </div>
          )}
        </div>

        {/* User Details */}
        {selectedUser && (
          <div className="border rounded-lg p-6 bg-white sticky top-4">
            <UserActionPanel
              user={selectedUser}
              onBan={handleBanUser}
              onUnban={handleUnbanUser}
              loading={actionLoading === selectedUser.id}
            />
          </div>
        )}
      </div>
    </div>
  );
};

interface UserActionPanelProps {
  user: UserWithActions;
  onBan: (userId: string, reason: string, duration?: number) => void;
  onUnban: (userId: string, reason?: string) => void;
  loading: boolean;
}

const UserActionPanel: React.FC<UserActionPanelProps> = ({
  user,
  onBan,
  onUnban,
  loading
}) => {
  const [action, setAction] = useState<'ban' | 'unban' | null>(null);
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState<number | undefined>(undefined);

  const handleSubmit = () => {
    if (action === 'ban' && reason) {
      onBan(user.id, reason, duration);
    } else if (action === 'unban') {
      onUnban(user.id, reason);
    }
    setAction(null);
    setReason('');
    setDuration(undefined);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center space-x-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{user.displayName}</h3>
          {user.banned && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
              Banned
            </span>
          )}
        </div>
        
        <div className="space-y-3 text-sm">
          <div>
            <strong className="text-gray-700">Email:</strong>
            <span className="ml-2 text-gray-600">{user.email}</span>
          </div>
          <div>
            <strong className="text-gray-700">Auth Provider:</strong>
            <span className="ml-2 text-gray-600 capitalize">{user.authProvider}</span>
          </div>
          <div>
            <strong className="text-gray-700">Subscription:</strong>
            <span className="ml-2 text-gray-600 capitalize">{user.subscriptionTier}</span>
          </div>
          <div>
            <strong className="text-gray-700">Gold Coins:</strong>
            <span className="ml-2 text-gray-600">{user.goldCoins}</span>
          </div>
          <div>
            <strong className="text-gray-700">Far-Away Tokens:</strong>
            <span className="ml-2 text-gray-600">{user.farAwayTokens}</span>
          </div>
          <div>
            <strong className="text-gray-700">Joined:</strong>
            <span className="ml-2 text-gray-600">{safeToDate(user.createdAt).toLocaleDateString()}</span>
          </div>
          <div>
            <strong className="text-gray-700">Last Active:</strong>
            <span className="ml-2 text-gray-600">{safeToDate(user.updatedAt).toLocaleDateString()}</span>
          </div>

          {user.banned && (
            <div className="bg-red-50 rounded-lg p-3 mt-4">
              <h4 className="font-medium text-red-900 mb-2">Ban Information</h4>
              <div className="space-y-1 text-sm text-red-700">
                <p><strong>Reason:</strong> {user.banReason}</p>
                <p><strong>Banned At:</strong> {user.bannedAt ? new Date(user.bannedAt).toLocaleString() : 'Unknown'}</p>
                {user.banUntil && (
                  <p><strong>Ban Until:</strong> {new Date(user.banUntil).toLocaleString()}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {!action && (
        <div className="space-y-3">
          {!user.banned ? (
            <button
              onClick={() => setAction('ban')}
              disabled={loading}
              className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              Ban User
            </button>
          ) : (
            <button
              onClick={() => setAction('unban')}
              disabled={loading}
              className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              Unban User
            </button>
          )}
        </div>
      )}

      {/* Action Form */}
      {action && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {action === 'ban' ? 'Ban Reason *' : 'Unban Reason (Optional)'}
            </label>
            {action === 'ban' ? (
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              >
                <option value="">Select a reason</option>
                <option value="spam_reports">Spam Reports</option>
                <option value="inappropriate_content">Inappropriate Content</option>
                <option value="harassment">Harassment</option>
                <option value="fake_reports">Fake Reports</option>
                <option value="terms_violation">Terms of Service Violation</option>
                <option value="other">Other</option>
              </select>
            ) : (
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Optional reason for unbanning..."
              />
            )}
          </div>

          {action === 'ban' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ban Duration (Optional)
              </label>
              <select
                value={duration || ''}
                onChange={(e) => setDuration(e.target.value ? parseInt(e.target.value) : undefined)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">Permanent</option>
                <option value="1">1 Day</option>
                <option value="7">1 Week</option>
                <option value="30">1 Month</option>
                <option value="90">3 Months</option>
              </select>
            </div>
          )}

          <div className="flex space-x-3">
            <button
              onClick={handleSubmit}
              disabled={loading || (action === 'ban' && !reason)}
              className={`flex-1 px-4 py-2 rounded-md text-white disabled:opacity-50 ${
                action === 'ban'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {loading ? 'Processing...' : `Confirm ${action === 'ban' ? 'Ban' : 'Unban'}`}
            </button>
            <button
              onClick={() => {
                setAction(null);
                setReason('');
                setDuration(undefined);
              }}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};